import { useCallback, useEffect, useRef, useState } from 'react';

import { useAIPageEmojiSuggestion } from '@atlassian/ai-image-generation/use-ai-page-emoji-suggestion';

import { isExperimentEnabled } from '@confluence/feature-experiments';

import type { AIEmojiSuggestionSource } from '../../types/AIEmojiSuggestionSource.ts';

type UseAIEmojiSuggestionForTitleGenArgs = {
	contentId: string;
	title: string | null | undefined;
	isTitleGenerating: boolean | undefined;
	locale: string | undefined;
	pageAlreadyHasEmoji: boolean | undefined;
};

export type UseAIEmojiSuggestionForTitleGenReturn = {
	aiSuggestedEmojiId: string | undefined;
	aifcSuggestionInFlight: boolean;
	aiEmojiSuggestionSource: AIEmojiSuggestionSource | undefined;
	aiEmojiSuggestionSessionId: number | undefined;
	hasPasteAutoTitleEmojiAppliedThisSession: boolean;
	onSuggestTitleClicked: (() => void) | undefined;
	onPasteAutoTitleEmojiApplied: (() => void) | undefined;
};

/**
 * Owns the AI-emoji suggestion state for the paste / suggest-title title-gen flows.
 * Watches `isTitleGenerating` rising and falling edges to bump a session id, call
 * the suggestion backend on completion, and reset state on each new session.
 */
export const useAIEmojiSuggestionForTitleGen = ({
	contentId,
	title,
	isTitleGenerating,
	locale,
	pageAlreadyHasEmoji,
}: UseAIEmojiSuggestionForTitleGenArgs): UseAIEmojiSuggestionForTitleGenReturn => {
	const { suggestEmoji: suggestEmojiFromPasteTitle, reset: resetEmojiSuggestionHook } =
		useAIPageEmojiSuggestion({ contentId });

	const [pasteAutoTitleSuggestedEmojiId, setPasteAutoTitleSuggestedEmojiId] = useState<
		string | undefined
	>(undefined);
	const [pasteAutoTitleEmojiInFlight, setPasteAutoTitleEmojiInFlight] = useState(false);
	const [pasteAutoTitleSessionId, setPasteAutoTitleSessionId] = useState(0);
	const [hasPasteAutoTitleEmojiAppliedThisSession, setPasteAutoTitleEmojiAppliedThisSession] =
		useState(false);

	const isPasteEmojiSuggestionEnabled =
		isExperimentEnabled('confluence_ai_title_generation_on_paste_standard') &&
		isExperimentEnabled('cc_page_experiences_blank_paste_suggested_emoji');

	const aiSuggestedEmojiId = isPasteEmojiSuggestionEnabled
		? pasteAutoTitleSuggestedEmojiId
		: undefined;
	const aifcSuggestionInFlight = isPasteEmojiSuggestionEnabled && pasteAutoTitleEmojiInFlight;

	const lastTriggerSourceRef = useRef<AIEmojiSuggestionSource>('pasteAutoTitle');
	const [currentTriggerSource, setCurrentTriggerSource] =
		useState<AIEmojiSuggestionSource>('pasteAutoTitle');
	const onSuggestTitleClicked = useCallback(() => {
		lastTriggerSourceRef.current = 'suggestTitle';
	}, []);
	const onPasteAutoTitleEmojiApplied = useCallback(
		() => setPasteAutoTitleEmojiAppliedThisSession(true),
		[],
	);

	const wasTitleGeneratingRef = useRef<boolean>(false);
	useEffect(() => {
		if (!isPasteEmojiSuggestionEnabled) {
			wasTitleGeneratingRef.current = isTitleGenerating ?? false;
			return;
		}
		const generating = isTitleGenerating ?? false;
		const justStarted = !wasTitleGeneratingRef.current && generating;
		const justFinished = wasTitleGeneratingRef.current && !generating;
		wasTitleGeneratingRef.current = generating;

		if (justStarted) {
			setPasteAutoTitleSessionId((id) => id + 1);
			setPasteAutoTitleSuggestedEmojiId(undefined);
			setPasteAutoTitleEmojiInFlight(false);
			setPasteAutoTitleEmojiAppliedThisSession(false);
			setCurrentTriggerSource(lastTriggerSourceRef.current);
			lastTriggerSourceRef.current = 'pasteAutoTitle';
			resetEmojiSuggestionHook?.();
			return;
		}

		if (!justFinished) {
			return;
		}
		const finalTitle = (title ?? '').trim();
		if (!finalTitle) {
			return;
		}
		if (pageAlreadyHasEmoji) {
			return;
		}
		setPasteAutoTitleEmojiInFlight(true);
		void suggestEmojiFromPasteTitle({ title: finalTitle, locale })
			.then((emojiId) => {
				if (!emojiId) {
					return;
				}
				setPasteAutoTitleSuggestedEmojiId(emojiId);
			})
			.catch(() => {
				// Swallow — suggestion is best-effort and the upstream hook already
				// reports errors via its own telemetry. We just need to make sure the
				// in-flight state below is still cleared.
			})
			.finally(() => {
				// Always clear in-flight state, even if the suggestion request rejects,
				// so aifcSuggestionInFlight does not stay permanently true for the session.
				setPasteAutoTitleEmojiInFlight(false);
			});
	}, [
		isTitleGenerating,
		isPasteEmojiSuggestionEnabled,
		title,
		locale,
		suggestEmojiFromPasteTitle,
		resetEmojiSuggestionHook,
		pageAlreadyHasEmoji,
	]);

	return {
		aiSuggestedEmojiId,
		aifcSuggestionInFlight,
		aiEmojiSuggestionSource: isPasteEmojiSuggestionEnabled ? currentTriggerSource : undefined,
		aiEmojiSuggestionSessionId: isPasteEmojiSuggestionEnabled ? pasteAutoTitleSessionId : undefined,
		hasPasteAutoTitleEmojiAppliedThisSession: isPasteEmojiSuggestionEnabled
			? hasPasteAutoTitleEmojiAppliedThisSession
			: false,
		onSuggestTitleClicked: isPasteEmojiSuggestionEnabled ? onSuggestTitleClicked : undefined,
		onPasteAutoTitleEmojiApplied: isPasteEmojiSuggestionEnabled
			? onPasteAutoTitleEmojiApplied
			: undefined,
	};
};
